import { Empty, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import type { AuditLogEntry } from '@/api/workflows';

const { Text } = Typography;

/** Items + loading owned by the page shell, mirroring {@link RecentDecisions}. */
type Props = {
  items: AuditLogEntry[] | null;
  loading: boolean;
  /**
   * True when a search or date range is active. Only changes the empty state:
   * "no matches" and "nothing has happened yet" look identical otherwise, and
   * the second reads as a broken page when you have in fact just filtered
   * everything out.
   */
  filtering?: boolean;
};

// Friendly label + tag color per action namespace. Unknown actions fall back to the raw
// dotted name with a neutral tag, so a newly-added AuditAction still renders sensibly.
const ACTION_META: Record<string, { label: string; color: string }> = {
  'workflow.created': { label: 'Created', color: 'blue' },
  'workflow.updated': { label: 'Updated', color: 'geekblue' },
  'workflow.autosaved': { label: 'Autosaved', color: 'default' },
  'workflow.submitted': { label: 'Submitted', color: 'gold' },
  'workflow.approved': { label: 'Approved', color: 'green' },
  'workflow.rejected': { label: 'Rejected', color: 'red' },
  'workflow.cloned': { label: 'Cloned', color: 'cyan' },
  'workflow.deleted': { label: 'Deleted', color: 'volcano' },
  'workflow.reverted': { label: 'Reverted', color: 'purple' },
  'workflow.activated': { label: 'Published', color: 'green' },
  'workflow.deactivated': { label: 'Unpublished', color: 'orange' },
  'workflow.reordered': { label: 'Reordered', color: 'default' },
};

export function AuditLog({ items, loading, filtering = false }: Props) {
  const { t, i18n } = useTranslation();

  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: t('workflow.versions.createdAt', { defaultValue: 'When' }),
      key: 'at',
      width: 180,
      render: (_: unknown, row) => new Date(row.at).toLocaleString(i18n.language),
    },
    {
      title: t('audit.action', { defaultValue: 'Action' }),
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (action: string) => {
        const meta = ACTION_META[action];
        return <Tag color={meta?.color ?? 'default'}>{meta?.label ?? action}</Tag>;
      },
    },
    // Actor is captured as three separate columns (Name / Email / Role) so the
    // reviewer can scan by whichever handle they know the person by. All three
    // fall back gracefully — legacy rows written before actorName/actorEmail
    // landed show the opaque Entra oid under Name so the row is never blank.
    {
      title: t('audit.actorName', { defaultValue: 'Name' }),
      key: 'actorName',
      render: (_: unknown, row) => <Text>{row.actorName || row.actorOid || '—'}</Text>,
    },
    {
      title: t('audit.actorEmail', { defaultValue: 'Email' }),
      key: 'actorEmail',
      render: (_: unknown, row) =>
        row.actorEmail ? (
          <Text copyable={{ text: row.actorEmail }}>{row.actorEmail}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('audit.actorRole', { defaultValue: 'Role' }),
      key: 'actorRole',
      width: 120,
      render: (_: unknown, row) =>
        row.actorRole ? (
          <Tag color={row.actorRole === 'Approver' ? 'geekblue' : 'default'}>{row.actorRole}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('audit.entity', { defaultValue: 'Entity' }),
      key: 'entity',
      // Prefer the snapshotted friendly name (workflow app name / element name); legacy
      // rows written before the name was captured fall back to the raw id.
      render: (_: unknown, row) => (
        <>
          <Text>{row.entityName || row.entityId}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.entityType}
          </Text>
        </>
      ),
      ellipsis: { showTitle: true },
    },
    {
      title: t('audit.details', { defaultValue: 'Details' }),
      key: 'details',
      ellipsis: { showTitle: true },
      // before→after captures the transition detail (e.g. rejection comment, new label).
      render: (_: unknown, row) => {
        if (row.before && row.after) return `${row.before} → ${row.after}`;
        return row.after || row.before || '—';
      },
    },
  ];

  if (loading && items === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (items && items.length === 0) {
    return (
      <Empty
        description={
          filtering
            ? t('audit.emptyFiltered', {
                defaultValue: 'No audit events match your search.',
              })
            : t('audit.empty', { defaultValue: 'No audit events yet' })
        }
      />
    );
  }

  return (
    <Table<AuditLogEntry>
      rowKey={(row) => `${row.at}-${row.action}-${row.entityId}`}
      columns={columns}
      dataSource={items ?? []}
      loading={loading}
      pagination={{ pageSize: 20 }}
      size="small"
    />
  );
}
